import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { FormulaModeloEmpresaService } from '@shared/services/apis/motor-fiscal/formula-modelo-empresa.service';
import { NaturezaOperacaoService } from '@shared/services/apis/motor-fiscal/natureza-operacao.service';
import { NaturezaOperacaoBulkDeleteRequest } from '@shared/services/requests/motor-fiscal/natureza-operacao-bulk-delete.request';
import { FormulaCatalogoGrupoStruct } from '@shared/services/structs/formula/formula-catalogo-grupo.struct';
import { HeaderComponent } from '@shared/components/header/header.component';
import { TabsScrollDirective } from '@shared/directives/tabs-scroll.directive';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { NaturezasListaComponent } from './tabs/naturezas-lista/naturezas-lista.component';
import { FormulasTabComponent } from './tabs/formulas-lista/formulas-lista.component';

@Component({
    selector: 'app-natureza-operacao-lista',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTabsModule,
        MatDialogModule,
        MatMenuModule,
        MatTooltipModule,
        TabsScrollDirective,
        HeaderComponent,
        NaturezasListaComponent,
        FormulasTabComponent,
    ],
    templateUrl: './natureza-operacao-lista.component.html',
    styleUrl: './natureza-operacao-lista.component.css',
})
export class NaturezaOperacaoListaComponent {
    public moduloEnum = ModuloEnum.Configuracoes;
    // Mantém o item "Notas Fiscais" destacado no menu ao entrar na listagem, como as demais telas
    // filhas de Notas Fiscais (emissores, certificado, regras). Antes usava Configuracoes_NaturezaOperacao,
    // que não corresponde a nenhum item do menu e por isso removia o destaque.
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;

    public _gruposFormulas: FormulaCatalogoGrupoStruct[] = [];
    public _isLoadingFormulas: boolean = false;
    public _formulasCarregadas: boolean = false;

    @ViewChild(NaturezasListaComponent) naturezasListaComponent!: NaturezasListaComponent;

    constructor(
        private router: Router,
        private formulaService: FormulaCatalogoService,
        private formulaModeloEmpresaService: FormulaModeloEmpresaService,
        private naturezaService: NaturezaOperacaoService,
        private alertService: AlertService,
        private dialog: MatDialog,
    ) { }

    public get selection() {
        return this.naturezasListaComponent?.selection;
    }

    public podeExcluirSelecionados(): boolean {
        if (!this.selection || !this.selection.hasValue()) {
            return false;
        }
        return this.selection.selected.every(item => item.tiposPadrao.length === 0);
    }

    public getExcluirTooltip(): string {
        if (!this.selection || !this.selection.hasValue()) {
            return '';
        }
        const hasPadrao = this.selection.selected.some(item => item.tiposPadrao.length > 0);
        return hasPadrao ? 'Não é possível excluir uma natureza de operação padrão.' : '';
    }

    public excluirSelecionados(): void {
        if (!this.selection || !this.selection.hasValue()) {
            return;
        }

        const selecionadas = this.selection.selected;
        const total = selecionadas.length;

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar Exclusão em Massa',
                description: `Confirma a exclusão de ${total} natureza(s) de operação selecionada(s)? Esta ação não poderá ser desfeita.`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.confirm) {
                const request: NaturezaOperacaoBulkDeleteRequest = {
                    ids: selecionadas.map(item => item.idNaturezaOperacao)
                };
                this.naturezaService.bulkDelete(request).subscribe({
                    next: response => {
                        // A exclusão em massa é parcial: pode excluir parte da seleção e ainda
                        // retornar erro. Por isso a listagem recarrega em qualquer caso, para não
                        // deixar a tela mostrando naturezas que já foram excluídas.
                        this.naturezasListaComponent.clickPesquisar();
                        if (response.isError) {
                            this.alertService.show('Atenção', response.errorDescription || 'Erro ao excluir selecionados', AlertType.warning);
                            return;
                        }
                        this.alertService.show('Sucesso', 'Naturezas excluídas com sucesso.', AlertType.success);
                    },
                    error: err => {
                        this.alertService.show('Erro inesperado', err.message || 'Erro ao excluir selecionados', AlertType.error);
                    }
                });
            }
        });
    }

    public onTabFormulasAtivada(): void {
        if (!this._formulasCarregadas) {
            this._isLoadingFormulas = true;
            this.formulaService.getContexto('natureza-operacao').subscribe({
                next: response => {
                    this._isLoadingFormulas = false;
                    if (response.isError) {
                        this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar fórmulas', AlertType.error);
                        return;
                    }
                    this._gruposFormulas = response.grupos;
                    this._formulasCarregadas = true;
                },
                error: err => {
                    this._isLoadingFormulas = false;
                    this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar catálogo', AlertType.error);
                },
            });
        }
    }

    public alterarModeloEmpresa(selecao: { chaveFormula: string; chaveFormulaModelo: string }): void {
        this.formulaModeloEmpresaService.alterar(selecao.chaveFormula, selecao.chaveFormulaModelo).subscribe({
            next: response => {
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription || 'Não foi possível alterar o modelo.', AlertType.error);
                    return;
                }
                this._formulasCarregadas = false;
                this.onTabFormulasAtivada();
                this.alertService.show('Sucesso', 'Modelo da fórmula atualizado.', AlertType.success);
            },
            error: () => this.alertService.show('Erro', 'Não foi possível alterar o modelo.', AlertType.error),
        });
    }

    public novaNatureza(): void {
        this.router.navigate(['/configuracoes/notas-fiscais/natureza-operacao/cadastrar']);
    }

    public voltar(): void {
        this.router.navigate(['/configuracoes/notas-fiscais']);
    }

    public abrirTabelaDifal(): void {
        this.router.navigate(['/configuracoes/notas-fiscais/tabela-difal']);
    }

    public abrirRegrasTributarias(): void {
        this.router.navigate(['/configuracoes/reforma-tributaria/regras']);
    }

    public avisoEmBreve(): void {
        this.alertService.show('Em breve', 'Este fluxo será implementado futuramente.', AlertType.info);
    }
}
